// ============================================================
// Sassy Full Stack Fitness — The Menopause Matrix
// MOCK DATA + CLIENT-SIDE SCORING ENGINE
// NOTE: This is FRONTEND MOCK. The real scoring engine will run
// server-side (FastAPI) once backend is built. Results are stored
// in localStorage here so the results page works end-to-end.
// ============================================================

// --- Brand logo ---
export const LOGO =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_wellness-refresh-16/artifacts/gp5w3zkh_Alternated_Logo-removebg-preview.png";

// --- Brand image assets (Sandra) ---
// Hero = gold competition bikini; Portrait = white tank (full PNG, arms & hands visible)
export const HERO_IMAGE = "/images/sandra-hero.webp";
export const SANDRA_STAGE_IMAGE = "/images/sandra-stage.webp";
export const SANDRA_AVATAR = "/images/sandra-casual.webp";
// Results page "A quick word from Sandra" video. Leave empty until the video is ready.
// Accepts a direct MP4 URL (shows gold play button; add captions .vtt below) or a YouTube/Vimeo link.
export const SANDRA_VIDEO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/sandra-video`;
export const SANDRA_VIDEO_CAPTIONS_URL = `${process.env.REACT_APP_BACKEND_URL}/api/sandra-video/captions.vtt`;
export const SANDRA_VIDEO_POSTER = "/images/sandra-video-poster.jpg";
export const SANDRA_VIDEO_PORTRAIT = true;
export const SANDRA_PORTRAIT = SANDRA_STAGE_IMAGE;
export const SANDRA_PORTRAIT_2 =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_wellness-refresh-16/artifacts/cjb5ufca_3.webp";

// --- Calendar booking link (Calendly) ---
export const CALENDAR_URL_PRIORITY =
  "https://calendly.com/sassyfullstackfitness/30min";
export const CALENDAR_URL_STANDARD =
  "https://calendly.com/sassyfullstackfitness/30min";

// --- Explainer video ---
// Served via our backend proxy (/api/video) which re-serves the Dropbox file
// as clean video/mp4 with range support (Dropbox's own headers break <video>).
export const VIDEO_SRC = `${process.env.REACT_APP_BACKEND_URL}/api/video`;
export const VIDEO_POSTER =
  "https://i.vimeocdn.com/video/2174823749-5190d13e58fa218c6e2cc569bb9bdd5fc37ec387bef7865847f42fa9f7349e94-d?mw=1400&q=90";

// --- 22 symptoms (flat order, DO NOT group by pillar on Step 1) ---
export const SYMPTOMS = [
  { id: "belly_fat", label: "Belly fat that won't budge" },
  { id: "cant_fall_asleep", label: "Can't fall asleep" },
  { id: "same_eating_gaining", label: "Eating the same as always and still gaining" },
  { id: "hot_flashes", label: "Hot flashes" },
  { id: "lost_tone", label: "Lost my muscle tone" },
  { id: "bloated", label: "Bloated no matter what I eat" },
  { id: "awake_2am", label: "Wide awake at 2–3am" },
  { id: "cravings", label: "Sugar and carb cravings I can't shut off" },
  { id: "winded", label: "Winded from things that used to be easy" },
  { id: "night_sweats", label: "Waking up drenched" },
  { id: "losing_strength", label: "Losing strength" },
  { id: "clothes_fit", label: "Clothes fit differently at the same weight" },
  { id: "fatigue", label: "Constant fatigue that sleep doesn't fix" },
  { id: "brain_fog", label: "Brain fog and losing words" },
  { id: "achy_joints", label: "Achy, creaky joints" },
  { id: "food_intolerance", label: "Foods that never bothered me now do" },
  { id: "crash_after_eating", label: "Crashing an hour after I eat" },
  { id: "sore_for_days", label: "Sore for days after a workout" },
  { id: "mood", label: "Mood swings, anxiety, or a short fuse" },
  { id: "calorie_cut_nothing", label: "Cutting calories and nothing happens" },
  { id: "low_libido", label: "Low libido" },
  { id: "stairs_hills", label: "Stairs and hills that never used to register" },
];

// --- Pillars (weight order matters for tie-breaking) ---
export const PILLARS = [
  { id: "nutrition", name: "Nutrition", weight: 25, color: "#D4AF37" },
  { id: "hormonal", name: "Hormonal", weight: 20, color: "#C77DFF" },
  { id: "metabolic", name: "Metabolic", weight: 20, color: "#FF7A45" },
  { id: "resistance", name: "Muscle & Strength", weight: 15, color: "#4EA8DE" },
  { id: "recovery", name: "Rest & Recovery", weight: 10, color: "#5BC0BE" },
  { id: "gut", name: "Gut Health", weight: 5, color: "#8AC926" },
  { id: "cardiovascular", name: "Cardiovascular Activity", weight: 5, color: "#FF5D8F" },
];

// --- Symptom -> pillar map ---
export const SYMPTOM_TO_PILLAR = {
  same_eating_gaining: "nutrition",
  cravings: "nutrition",
  calorie_cut_nothing: "nutrition",
  hot_flashes: "hormonal",
  night_sweats: "hormonal",
  mood: "hormonal",
  low_libido: "hormonal",
  brain_fog: "hormonal",
  belly_fat: "metabolic",
  clothes_fit: "metabolic",
  crash_after_eating: "metabolic",
  lost_tone: "resistance",
  losing_strength: "resistance",
  achy_joints: "resistance",
  cant_fall_asleep: "recovery",
  awake_2am: "recovery",
  fatigue: "recovery",
  sore_for_days: "recovery",
  bloated: "gut",
  food_intolerance: "gut",
  winded: "cardiovascular",
  stairs_hills: "cardiovascular",
};

// --- Results-page pillar cards (verbatim §6.4) ---
export const PILLAR_CARDS = {
  nutrition: {
    heading: "Nutrition",
    body: "What worked at 35 works against you at 50. Protein targets, timing, and intake built for the body you have now, not a smaller version of an old plan.",
  },
  hormonal: {
    heading: "Hormonal",
    body: "The system a gym can't reach. Labs and provider oversight through our vetted partners, coordinated with everything else you're doing.",
  },
  metabolic: {
    heading: "Metabolic",
    body: "Your metabolism didn't break. It changed the rules. Metabolic support through our medical partners, paired with the nutrition and training that make it hold.",
  },
  resistance: {
    heading: "Muscle & Strength",
    body: "Losing tone isn't cosmetic. It's why you feel fragile. Progressive strength work that rebuilds what mid-life takes, without wrecking your joints.",
  },
  recovery: {
    heading: "Rest & Recovery",
    body: "You cannot out-train exhaustion. Sleep quality, stress load, and recovery capacity: usually the first domino, and the one most programs skip.",
  },
  gut: {
    heading: "Gut Health",
    body: "Bloating and new food reactions aren't in your head. Digestion drives inflammation and how much of your nutrition you actually absorb.",
  },
  cardiovascular: {
    heading: "Cardiovascular Activity",
    body: "Conditioning that protects your heart and your endurance, dosed so it doesn't drive up the stress hormones already working against you.",
  },
};

export const STAGE_OPTIONS = [
  { id: "still_cycling", label: "Still cycling, but it's changed" },
  { id: "cycles_stopped", label: "Cycles have stopped" },
  { id: "post", label: "Post-menopause" },
  { id: "unsure", label: "Not sure" },
];

export const HRT_OPTIONS = [
  { id: "on_hrt", label: "On HRT now" },
  { id: "considering", label: "Considering it" },
  { id: "no", label: "No" },
  { id: "tried_failed", label: "Tried it, didn't work" },
];

// --- FAQ (verbatim §3.8) ---
export const FAQ = [
  {
    q: "Do I have to be in menopause for this to help?",
    a: "No. Many of our clients are perimenopausal and didn't know it. The changes often start years before cycles stop. If your body stopped responding to what used to work, this applies to you.",
  },
  {
    q: "Is this just another meal plan and workout app?",
    a: "No. Nutrition and training are two of the seven systems. Working on those alone is why most programs stall out for women over 40.",
  },
  {
    q: "I've already researched all of this. Why do I need a coach?",
    a: [
      { t: "Because information was never your problem.", strong: true },
      { t: "AI will write you a training plan. It will write you a meal plan. Both will probably be reasonable. What it can't do is watch what happens next." },
      { t: "It can't order a lab panel or read it with a provider who knows your history. It can't see that you've hit your protein five days running but your strength hasn't moved and changed the program because of it. It can't check in on Wednesday when the week is going sideways or answer the message you send at 9pm from the grocery store or notice you've gone quiet and text you before two weeks become two months." },
      { t: "And it can't build a relationship with you. The kind where someone knows your history, knows what you're capable of on a hard week, and has earned enough of your trust that you listen when she tells you to push. AI waits to be asked. It has no stake in whether you actually do it." },
      { t: "Sandra does. She builds the plan, stays in contact while you run it, watches the numbers: training, nutrition, labs, energy, recovery and adjusts that plan when your body says to. And she's expecting you next week." },
      { t: "That's the difference between having information and having a transformation!", strong: true },
    ],
  },
  {
    q: "Do you prescribe hormones or medication?",
    a: "We don't. Prescribing decisions are made by licensed providers. If you choose, we coordinate with our vetted medical, lab and pharmacy partners so your coaching plan and your medical care line up instead of working against each other.",
  },
  {
    q: "Can you help with peptides or medical weight-loss?",
    a: "Yes, the same way we handle labs and hormones. We don't prescribe. Through our vetted medical and pharmacy partners, on your behalf, we can coordinate peptide therapy and medical weight-loss options, with all prescribing decisions made by a licensed provider based on your labs.",
  },
  {
    q: "Do you provide supplement recommendations?",
    a: "Absolutely, and you can get them direct from us. We recommend supplements only when they'll actually move the needle for your plan, and we keep it to the most researched, evidence-based options. Everything in our store is manufactured in the U.S. in FDA-registered facilities under strict cGMP (Good Manufacturing Practice) standards, so you know exactly what you're getting.",
  },
  {
    q: "What if I'm already on HRT?",
    a: "Good, that's one system you are already addressing. We will coordinate with your provider or ours if you choose to change. We then build the training, nutrition and recovery, gut health protocol and cardiovascular activity around it so everything works together.",
  },
  {
    q: "What happens on the call?",
    a: "Thirty minutes. Sandra reads your Matrix before you talk. You'll leave knowing which system to start with and why, whether or not you ever work with us.",
  },
  {
    q: "How much does it cost?",
    a: "DIY coaching programs start at $199. One-to-one coaching is quoted after an initial consultation and assessment, based on which program is the best fit and the length of engagement. We'll walk through the options during the initial consultation. The Matrix and the call are both free.",
  },
];

// --- Testimonials (from start.sassyfitness.com — client's own site content) ---
export const TESTIMONIALS = [
  {
    name: "Sherry Jacques",
    age: 57,
    role: "Retired Attorney",
    chip: "Not a gym person",
    image:
      "https://customer-assets-jt897jd0.emergentagent.net/job_custom-landing-14/artifacts/l1s799he_Sherry.png",
    quote:
      "I started in my mid-50s (not athletic, definitely not a gym person). Sandra met me where I was, and two and a half years later I'm stronger and more confident in my body than I ever imagined I'd be.",
  },
  {
    name: "Ashley Meadows",
    age: 58,
    role: "Fitness Instructor in the '90s",
    chip: "Lost 10 lbs with real muscle tone",
    image:
      "https://customer-assets-jt897jd0.emergentagent.net/job_custom-landing-14/artifacts/4rja0owh_Ashley.png",
    quote:
      "In six months I lost 10 pounds, gained real muscle tone, and my posture is noticeably better. Sandra followed every single workout and every question, and the habits she built with me have stuck ever since.",
  },
  {
    name: "Mary Helen Richards",
    age: 62,
    role: "Muscle & Mindset",
    chip: "Muscle growth achieved",
    image:
      "https://customer-assets-jt897jd0.emergentagent.net/job_custom-landing-14/artifacts/u58znc6l_Screenshot_20260809_120353_Instagram.jpg",
    quote:
      "The muscle growth and transformation of my body has surpassed my expectations. Trusting Sandra's wisdom and letting go of old patterns changed me on the inside too.",
  },
  {
    name: "Cynthia Nowell",
    age: 73,
    role: "Strength & Longevity",
    chip: "Stronger & healthier at 74",
    image:
      "https://customer-assets-jt897jd0.emergentagent.net/job_custom-landing-14/artifacts/3ce8pdx9_Screenshot_20260809_122644_Gmail.webp",
    quote:
      "I walked out of my doctor's office discouraged at 73. A year later I'm stronger, my labs have improved, and I'm proud of what we accomplished. Sandra has been a true blessing, and she can be yours too.",
  },
  {
    name: "Debbie Strain",
    age: 63,
    role: "Household CEO",
    chip: "All of my labs improved",
    image:
      "https://customer-assets-m6fa6gv7.emergentagent.net/job_wellness-refresh-16/artifacts/s1ww2t10_image.png",
    quote:
      "I just left my doctor's office with my new bloodwork results and everything is so much better. I'm so excited! I owe all of that to you. You've kept me motivated and on track, and I'm truly making a lifestyle change. Where I used to just go through the motions, now I'm very intentional. So thank you, thank you! Love you.",
  },
];

// --- Text-only testimonials (no photo) — rendered in a second row under TESTIMONIALS ---
export const TEXT_TESTIMONIALS = [
  {
    title: "Sandra Changed My Life!",
    name: "Monica Everheart",
    location: "Dothan, Alabama",
    quote:
      "Sandra's passion for fitness and for helping women become stronger, both physically and mentally, is contagious. She has helped influence the path my life and career took. If you are looking for someone who genuinely cares about helping you become the strongest version of yourself, I wholeheartedly recommend Sandra. She is an incredible trainer, mentor, and person.",
  },
  {
    title: "Absolutely Recommend!",
    name: "Jessica Lewis",
    location: "Sharpsburg, Georgia",
    quote:
      "Sandra went above and beyond to help my son and I reach our fitness and health goals. We were not active individuals and needed to make big changes. Her steady support, patience, understanding, strength and confidence in our ability to make improvements resulted in positive changes for us both. Would highly recommend her to anyone who is seeking to improve both physical and mental health. She has a fabulously rare, optimistic and faith-filled attitude that is sure to help you achieve your goals.",
  },
  {
    title: "Doesn't Get Any Better!",
    name: "Ashley Serrato",
    location: "Wicksburg, Alabama",
    quote:
      "Guaranteed results due to her years of experience, and a more positive outlook on life to go along with it. I lost 65lbs in 8 months and had more muscle mass than I ever had playing sports. She is the real deal.",
  },
  {
    title: "Top Class In My Book!",
    name: "Caron Watson",
    location: "Panama City Beach, Florida",
    quote:
      "I love training with Sandra! She makes the experience something to look forward. She is passionate about her clients and sets such a great example of the way she lives her life. I am proud of her faith and to call her a friend!",
  },
  {
    title: "The best!!",
    name: "Alexis Gregory",
    location: "Inlet Beach, Florida",
    quote:
      "Truly had the BEST experience with Sassy Fitness. Everything from my nutrition to training was fully customized for my goals, and I saw the most incredible results in not only how I looked, but most importantly how I felt. I'm thankful for her guidance and recommend her to everyone I know who wants real results!",
  },
];

// ============================================================
// SCORING ENGINE (mirrors spec §5.3 — will move server-side)
// ============================================================
export function computeMatrix({ checkedItems, primarySymptom, stage, hrtStatus }) {
  // Step 1: raw score per pillar
  const totalsInPillar = {};
  const checkedInPillar = {};
  PILLARS.forEach((p) => {
    totalsInPillar[p.id] = 0;
    checkedInPillar[p.id] = 0;
  });
  Object.values(SYMPTOM_TO_PILLAR).forEach((pid) => {
    totalsInPillar[pid] += 1;
  });
  checkedItems.forEach((id) => {
    const pid = SYMPTOM_TO_PILLAR[id];
    if (pid) checkedInPillar[pid] += 1;
  });

  const raw = {};
  let totalRaw = 0;
  PILLARS.forEach((p) => {
    raw[p.id] = (checkedInPillar[p.id] / totalsInPillar[p.id]) * p.weight;
    totalRaw += raw[p.id];
  });

  // Step 2: normalize to percentages
  const exactPct = {};
  PILLARS.forEach((p) => {
    exactPct[p.id] = totalRaw > 0 ? (raw[p.id] / totalRaw) * 100 : 0;
  });

  // Step 3: floor + distribute remainder by largest fractional remainder,
  // tie-break by weight order (nutrition first).
  const floors = {};
  let floorSum = 0;
  PILLARS.forEach((p) => {
    floors[p.id] = Math.floor(exactPct[p.id]);
    floorSum += floors[p.id];
  });
  let remainder = 100 - floorSum;
  const byFrac = PILLARS.map((p, idx) => ({
    id: p.id,
    frac: exactPct[p.id] - Math.floor(exactPct[p.id]),
    order: idx,
  })).sort((a, b) => (b.frac - a.frac) || (a.order - b.order));

  const pct = { ...floors };
  let i = 0;
  while (remainder > 0 && i < byFrac.length) {
    pct[byFrac[i].id] += 1;
    remainder -= 1;
    i += 1;
  }

  // Step 4: Primary System = pillar of the named primary symptom
  const primarySystem = SYMPTOM_TO_PILLAR[primarySymptom];

  // Step 5: Supporting = top 2 pct excluding primary (tie-break weight order)
  const supporting = PILLARS.filter((p) => p.id !== primarySystem && pct[p.id] > 0)
    .map((p, _i) => ({ id: p.id, pct: pct[p.id], order: PILLARS.findIndex((x) => x.id === p.id) }))
    .sort((a, b) => (b.pct - a.pct) || (a.order - b.order))
    .slice(0, 2)
    .map((x) => x.id);

  // Step 6: active count
  const activePillarCount = PILLARS.filter((p) => pct[p.id] > 0).length;

  // Lead score (internal)
  const cc = checkedItems.length;
  let symPts = 1;
  if (cc >= 15) symPts = 4;
  else if (cc >= 10) symPts = 3;
  else if (cc >= 5) symPts = 2;
  const stagePts = { unsure: 1, still_cycling: 2, cycles_stopped: 3, post: 3 }[stage] || 1;
  const hrtPts = { no: 1, on_hrt: 3, tried_failed: 3, considering: 4 }[hrtStatus] || 1;
  const leadScore = symPts + stagePts + hrtPts;
  let leadTier = "C";
  if (leadScore >= 8) leadTier = "A";
  else if (leadScore >= 5) leadTier = "B";

  return {
    pillar_percentages: pct,
    primary_system: primarySystem,
    supporting_systems: supporting,
    active_pillar_count: activePillarCount,
    checked_count: cc,
    lead_score: leadScore,
    lead_tier: leadTier,
  };
}

// --- localStorage persistence for mock results ---
export function generateResultId() {
  const s = () => Math.random().toString(36).slice(2);
  return (s() + s() + s()).slice(0, 24);
}

export function saveResult(record) {
  try {
    localStorage.setItem(`matrix_result_${record.result_id}`, JSON.stringify(record));
  } catch (e) {
    /* ignore */
  }
}

export function getResult(resultId) {
  try {
    const raw = localStorage.getItem(`matrix_result_${resultId}`);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

export function pillarName(id) {
  const p = PILLARS.find((x) => x.id === id);
  return p ? p.name : id;
}
export function pillarColor(id) {
  const p = PILLARS.find((x) => x.id === id);
  return p ? p.color : "#D4AF37";
}
